import { Button } from '@chakra-ui/button';
import { useColorMode, useColorModeValue } from '@chakra-ui/color-mode';
import { Flex, Box, Text, Heading, Stack } from '@chakra-ui/layout';
import layoutStore from '../stores/layoutStore';
import { motion, isValidMotionProp, AnimatePresence } from 'framer-motion';
import { Icon } from '@chakra-ui/icon';
import { MainButton, SearchIcon, TrashDockIcon, MotionBox } from './UiElements';
import React, { useState } from 'react';
import SearchModal from './SearchModal';
import NoteToolbar from './NoteToolbar';
import SelectionBar from './SelectionBar';
import useSelectStore from '../stores/selectStore';
import useDndStore from '../stores/dndStore';
import useWsGridStore from '../stores/wsGridStore';

const Dock = () => {
    const currentDock = layoutStore((state) => state.currentDock);
    const selectedIds = useSelectStore((state) => state.selectedIds);
    const isDragging = useDndStore((state) => state.globalIsDragging);
    const dockShadow = useColorModeValue(
        'drop-shadow(0 6px 8px rgba(166,166,167,0.52))',
        'drop-shadow(0 6px 8px rgba(19,20,24,0.95))'
    );

    let dockContent = {
        arrows: <ArrowKeys />,
        noteToolbar: <NoteToolbar />,
        selectionBar: <SelectionBar />
    };

    if (
        currentDock == null ||
        (currentDock === 'selectionBar' && !selectedIds.length) ||
        isDragging
    )
        return null;

    return (
        <Flex
            as={motion.div}
            initial={{ y: 100 }}
            animate={{
                y: 0,
                transition: {
                    type: 'spring',
                    damping: 14,
                    mass: 0.8,
                    stiffness: 200,
                    velocity: 18,
                    delay: 0.1
                    //delay: mountDone ? 0 : 0.45
                }
            }}
            bg="transparent"
            pos="fixed"
            w="calc(100vw - 16px)"
            left="0"
            right="0"
            bottom="0"
            alignItems="center"
            justifyContent="center"
            pointerEvents="none"
            zIndex="38"
            //onAnimationComplete={() => setMountDone(true)}
        >
            <Flex
                as={motion.div}
                layout
                id={'dock-container'}
                pointerEvents={'all'}
                mb="12px"
                bg="groupBg"
                border="var(--chakra-colors-teBorder)"
                //boxShadow="lg"
                filter={dockShadow}
                rounded="14px"
                cursor="pointer"
                maxW="calc(100vw - 32px)"
                position="relative"
                p="4px 6px"
            >
                <AnimatePresence>
                    <React.Fragment key={'dock-content'}>
                        <MotionBox
                            display="flex"
                            gap={'4px'}
                            maxH="48px"
                            as={motion.div}
                            layout="preserve-aspect"
                            //key="init-bar"
                            initial={{ scaleX: 0 }}
                            animate={{
                                scaleX: 1,
                                transition: { delay: 0.12, duration: 0.12 }
                                //transformOrigin: 'left'
                            }}
                            exit={{
                                scaleX: 0
                            }}
                            //@ts-ignore
                            transition={{
                                layout: {
                                    duration: 0.24,
                                    type: 'spring',
                                    bounce: 0.12
                                }
                            }}
                        >
                            {dockContent[currentDock]}
                        </MotionBox>
                    </React.Fragment>
                </AnimatePresence>
            </Flex>
        </Flex>
    );
};

export default Dock;

const SearchBar = ({ searchOpen, setSearchOpen }) => {
    const bg = useColorModeValue(
        'var(--chakra-colors-gray-100)',
        'var(--chakra-colors-whiteAlpha-50)'
    );
    const hoverBg = useColorModeValue(
        'var(--chakra-colors-gray-200)',
        'var(--chakra-colors-whiteAlpha-100)'
    );

    return (
        <>
            {/* <InputGroup size="sm" rounded="8px" variant="filled">
            <InputLeftElement pointerEvents="none">
                <Icon as={SearchIcon} />
            </InputLeftElement>
            <Input
                type="tel"
                // placeholder="Search"  
                size="sm"
                w="164px"
                rounded="8px"
            />
        </InputGroup> */}
            <AnimatePresence>
                {!searchOpen ? (
                    <Button
                        bg={bg}
                        w="164px"
                        rounded="8px"
                        maxH="32px"
                        px="8px"
                        cursor={'text'}
                        _hover={{ bg: hoverBg }}
                        onClick={() => setSearchOpen(true)}
                    >
                        <motion.div style={{ marginRight: 'auto' }}>
                            <Icon as={SearchIcon} />
                        </motion.div>
                    </Button>
                ) : (
                    <motion.div
                        layout={'position'}
                        initial={{ scaleX: 0.5, scaleY: 0.8 }}
                        animate={{ scaleX: 1, scaleY: 1 }}
                        exit={{ scaleX: 0.4, scaleY: 0.8 }}
                    >
                        <SearchModal />
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
};

const ArrowKeys = () => {
    const arrowKeys = ['up', 'down', 'left', 'right'];
    const onEnter = () => {
        let currentHighlight = useWsGridStore.getState().highlighted;
        useWsGridStore
            .getState()
            .changeWorkspace(currentHighlight.WSid, currentHighlight.cat);
    };
    const activeButton = useColorModeValue('gray.200', '#24272A');

    return (
        <>
            {arrowKeys.map((key) => (
                <Key key={key} arrow={key} />
            ))}

            <Button
                onClick={onEnter}
                size="sm"
                bg={'transparent'}
                _hover={{
                    bg: activeButton
                }}
                _active={{
                    bg: activeButton
                }}
                h="32px"
                rounded="8px"
                p="0 8px"
                alignItems={'center'}
                transition={'0.1s'}
                rightIcon={
                    <span style={{ opacity: 0.5, fontSize: '10px' }}>↵</span>
                }
            >
                Open
            </Button>
        </>
    );
};

const Key = ({ arrow }) => {
    const keyTapped = () => {
        useWsGridStore.getState().moveHighlight(arrow);
    };

    return (
        <MainButton
            icon={arrow}
            onClick={keyTapped}
            noBg={true}
            //mr={arrow === 'right' ? '' : '4px'}
        />
    );
};
